import React from 'react';
import toast from 'react-hot-toast';
import { ToastCard } from '../components/ToastCard';

const DEFAULT_DURATION = 4000;

function showStandardToast(message, type, duration, style) {
  const ms = typeof duration === 'number' && duration > 0 ? duration : DEFAULT_DURATION;
  return toast.custom(
    (t) =>
      React.createElement(
        'div',
        style ? { style } : null,
        React.createElement(ToastCard, {
          message: String(message ?? ''),
          type,
          visible: t.visible,
        }),
      ),
    { duration: ms },
  );
}

/**
 * Show a toast notification. Matches the previous showToast(message, type, duration) API.
 * All types use the same white-card + circular icon chrome (see ToastCard).
 *
 * @param {string} message - Message to show
 * @param {'success'|'error'|'info'|'warning'} [type='info'] - Toast type
 * @param {number} [duration] - Duration in ms
 * @param {{ anchor?: HTMLElement|React.RefObject, anchorPosition?: 'above'|'below' }} [options]
 */
export function showToast(message, type = 'info', duration = DEFAULT_DURATION, options = {}) {
  const kind =
    type === 'success' || type === 'error' || type === 'warning' || type === 'info'
      ? type
      : 'info';
  const anchorEl = options?.anchor?.current ?? options?.anchor;
  const anchorPosition = options?.anchorPosition ?? 'below';

  if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
    const rect = anchorEl.getBoundingClientRect();
    const gutter = 8;
    const screenPad = 12;
    const maxW = Math.min(420, window.innerWidth - screenPad * 2);
    const rawLeft = rect.left + rect.width / 2 - maxW / 2;
    const clampedLeft = Math.max(
      screenPad,
      Math.min(rawLeft, window.innerWidth - maxW - screenPad),
    );
    const isAbove = anchorPosition === 'above';
    const top = isAbove ? rect.top - gutter : rect.bottom + gutter;
    return showStandardToast(message, kind, duration, {
      position: 'fixed',
      top: `${top}px`,
      left: `${clampedLeft}px`,
      transform: isAbove ? 'translateY(-100%)' : 'none',
      zIndex: 2147483647,
      width: `${maxW}px`,
    });
  }

  return showStandardToast(message, kind, duration);
}
